// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * How use it:
 * 1. by dispatch:
 *      dispatch({ type: "xHeader/update", data: { key: "isStick", value: true } });
 *      data => it has "key" -> object key and value
 * 2. by handler:
 *      xHeaderUpdate({ key: "isStick", value: true });
 */

import { xHeaderHandlerType } from "@awc/App/Handlers/XNG/Header";
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft, original } from "immer";

export type xHeaderStateType = Readonly<{
    isSticky?: boolean;
    registerMenu?: string[];
    unregisterMenu?: string;
    headerHeight?: string;
    stickyHeader?: string;
    mobileSearch?: boolean;
    secondaryNavigationItems?: any[];
    categories?: any[];
}>;

export const initialHeaderState: xHeaderStateType = {
    isSticky: false,
    registerMenu: [],
    unregisterMenu: "",
    headerHeight: "",
    stickyHeader: "",
    mobileSearch: false,
    secondaryNavigationItems: [],
    categories: [],
};

const reducer = {
    "xHeader/update": (draft: Draft<xHeaderStateType>, action: { data: xHeaderHandlerType }) => {
        if (action.data.key) {
            if (action.data.key === "registerMenu") {
                draft["registerMenu"]?.push(action.data.value);
            } else if (action.data.key === "unregisterMenu") {
                const currentStore = original(draft) as xHeaderStateType;
                draft["registerMenu"] = currentStore.registerMenu?.filter((item: string) => action.data.value !== item);
            } else {
                draft[action.data.key] = action.data.value;
            }
        }
    },
    "xHeader/isSticky": (draft: Draft<xHeaderStateType>, action: { isSticky: boolean }) => {
        draft.isSticky = action.isSticky;
    },
    "xHeader/mobileSearch": (draft: Draft<xHeaderStateType>, action: { mobileSearch: boolean }) => {
        draft.mobileSearch = action.mobileSearch;
    },
    "xHeader/setSecondaryNavigationItems": (
        draft: Draft<xHeaderStateType>,
        action: { secondaryNavigationItems: any[] },
    ) => {
        draft.secondaryNavigationItems = action.secondaryNavigationItems;
    },
    "xHeader/setCategories": (draft: Draft<xHeaderStateType>, action: { categories: any[] }) => {
        draft.categories = action.categories;
    },
};

const xHeader = createTypedReducerWithImmer(initialHeaderState, reducer);

(reducers as any).xHeader = xHeader;

export type xHeaderState = {
    xHeader: xHeaderStateType;
} & ApplicationState;

type xHeaderReducers = {
    [key: string]: typeof xHeader;
};

export type xHeaderActions = Parameters<xHeaderReducers[keyof xHeaderReducers]>[1] | AnyAction;
